import { Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import ProtectedRoute from "../components/admin/SuperAdmin/ProtectedRoutes/ProtectedRoutes";
import Loader from "../components/loader/Loader";
import { AdminAllAgreements } from "../components/admin/SuperAdmin/Agreement/Agreements";
import { AdminCreateAgreement } from "../components/admin/SuperAdmin/Agreement/CreateAgreement";
import { AdminUpdateAgreement } from "../components/admin/SuperAdmin/Agreement/UpdateAgreement";
import { AdminAllAgreementInvoices } from "../components/admin/SuperAdmin/AgreementInvoices/AgreementInvoices";
import { AdminAgreementInvoicePayments } from "../components/admin/SuperAdmin/AgreementInvoicePayments/AgreementInvoicePayments";
import { AdminAgreementViewInvoice } from "../components/admin/SuperAdmin/AgreementInvoices/AgreementViewInvoice";
import { AdminViewInvoicePayment } from "../components/admin/SuperAdmin/AgreementInvoicePayments/ViewInvoicePayment";
import { AgreementCreatePayment } from "../components/admin/SuperAdmin/AgreementInvoicePayments/AgreementCreatePayment";

// Lazy-loaded Components
const AdminQuest = lazy(() =>
  import("../components/admin/SuperAdmin/Quest/Quests").then((module) => ({
    default: module.AdminQuest,
  }))
);
const AdminDashboard = lazy(() =>
  import("../components/admin/SuperAdmin/dashboard/Dashboard").then(
    (module) => ({
      default: module.AdminDashboard,
    })
  )
);
const AdminEditQuest = lazy(() =>
  import("../components/admin/SuperAdmin/Quest/EditQuest").then((module) => ({
    default: module.AdminEditQuest,
  }))
);
const AdminViewQuest = lazy(() =>
  import("../components/admin/SuperAdmin/Quest/ViewQuest").then((module) => ({
    default: module.AdminViewQuest,
  }))
);
const AdminCreateQuest = lazy(() =>
  import("../components/admin/SuperAdmin/Quest/CreateQuest").then((module) => ({
    default: module.AdminCreateQuest,
  }))
);
const AdminUser = lazy(() =>
  import("../components/admin/SuperAdmin/User/Users").then((module) => ({
    default: module.AdminUser,
  }))
);
const AdminStudent = lazy(() =>
  import("../components/admin/SuperAdmin/Student/Students").then((module) => ({
    default: module.AdminStudent,
  }))
);
const AdminTeacher = lazy(() =>
  import("../components/admin/SuperAdmin/Teacher/Teachers").then((module) => ({
    default: module.AdminTeacher,
  }))
);
const AdminParents = lazy(() =>
  import("../components/admin/SuperAdmin/Parents/Parents").then((module) => ({
    default: module.AdminParents,
  }))
);
const AdminNotifications = lazy(() =>
  import("../components/admin/SuperAdmin/Notifications/Notifications").then(
    (module) => ({ default: module.AdminNotifications })
  )
);
const AdminCreateTeacher = lazy(() =>
  import("../components/admin/SuperAdmin/Teacher/CreateTeacher").then(
    (module) => ({ default: module.AdminCreateTeacher })
  )
);
const AdminEditTeacher = lazy(() =>
  import("../components/admin/SuperAdmin/Teacher/EditTeacher").then(
    (module) => ({ default: module.AdminEditTeacher })
  )
);
const AdminCreateParent = lazy(() =>
  import("../components/admin/SuperAdmin/Parents/CreateParent").then(
    (module) => ({ default: module.AdminCreateParent })
  )
);
const AdminEditParent = lazy(() =>
  import("../components/admin/SuperAdmin/Parents/EditParent").then(
    (module) => ({ default: module.AdminEditParent })
  )
);
const AdminCreateStudent = lazy(() =>
  import("../components/admin/SuperAdmin/Student/CreateStudent").then(
    (module) => ({ default: module.AdminCreateStudent })
  )
);
const AdminEditStudent = lazy(() =>
  import("../components/admin/SuperAdmin/Student/EditStudent").then(
    (module) => ({ default: module.AdminEditStudent })
  )
);
const AdminCreateUser = lazy(() =>
  import("../components/admin/SuperAdmin/User/CreateUser").then((module) => ({
    default: module.AdminCreateUser,
  }))
);
const AdminEditUser = lazy(() =>
  import("../components/admin/SuperAdmin/User/EditUser").then((module) => ({
    default: module.AdminEditUser,
  }))
);
const AdminSchool = lazy(() =>
  import("../components/admin/SuperAdmin/School/Schools").then((module) => ({
    default: module.AdminSchool,
  }))
);
const AdminCreateSchool = lazy(() =>
  import("../components/admin/SuperAdmin/School/CreateSchool").then(
    (module) => ({ default: module.AdminCreateSchool })
  )
);
const AdminViewSchool = lazy(() =>
  import("../components/admin/SuperAdmin/School/ViewSchool").then((module) => ({
    default: module.AdminViewSchool,
  }))
);
const AdminViewParent = lazy(() =>
  import("../components/admin/SuperAdmin/Parents/ViewParent").then(
    (module) => ({
      default: module.AdminViewParent,
    })
  )
);
const AdminQuizzes = lazy(() =>
  import("../components/admin/SuperAdmin/Quizzes/Quizzes").then((module) => ({
    default: module.AdminQuizzes,
  }))
);
const AdminCreateQuizz = lazy(() =>
  import("../components/admin/SuperAdmin/Quizzes/CreateQuizz").then(
    (module) => ({ default: module.AdminCreateQuizz })
  )
);
const AdminViewStudent = lazy(() =>
  import("../components/admin/SuperAdmin/Student/ViewStudent").then(
    (module) => ({ default: module.AdminViewStudent })
  )
);
const AdminCreateNotification = lazy(() =>
  import(
    "../components/admin/SuperAdmin/Notifications/CreateNotification"
  ).then((module) => ({ default: module.AdminCreateNotification }))
);
const AdminSettings = lazy(() =>
  import("../components/admin/SuperAdmin/Account/Setting").then((module) => ({
    default: module.AdminSettings,
  }))
);
const AdminUserRoles = lazy(() =>
  import("../components/admin/SuperAdmin/Roles/UserRoles").then((module) => ({
    default: module.AdminUserRoles,
  }))
);
const AdminTickets = lazy(() =>
  import("../components/admin/SuperAdmin/Support/Tickets").then((module) => ({
    default: module.AdminTickets,
  }))
);
const AdminCreateRole = lazy(() =>
  import("../components/admin/SuperAdmin/Roles/CreateRole").then((module) => ({
    default: module.AdminCreateRole,
  }))
);
const AdminReports = lazy(() =>
  import("../components/admin/SuperAdmin/Report/Reports").then((module) => ({
    default: module.AdminReports,
  }))
);
const AdminCreateChapter = lazy(() =>
  import("../components/admin/SuperAdmin/Chapter/CreateChapter").then(
    (module) => ({ default: module.AdminCreateChapter })
  )
);
const AdminViewChapter = lazy(() =>
  import("../components/admin/SuperAdmin/Chapter/Chapters").then((module) => ({
    default: module.AdminViewChapter,
  }))
);
const AdminEditQuizz = lazy(() =>
  import("../components/admin/SuperAdmin/Quizzes/EditQuizz").then((module) => ({
    default: module.AdminEditQuizz,
  }))
);
const AdminViewQuizz = lazy(() =>
  import("../components/admin/SuperAdmin/Quizzes/ViewQuizz").then((module) => ({
    default: module.AdminViewQuizz,
  }))
);
const AdminEditChapter = lazy(() =>
  import("../components/admin/SuperAdmin/Chapter/EditChapter").then(
    (module) => ({ default: module.AdminEditChapter })
  )
);
const AdminCertificate = lazy(() =>
  import("../components/admin/SuperAdmin/Certificate/Certificate").then(
    (module) => ({ default: module.AdminCertificate })
  )
);
const AdminCreateCertificate = lazy(() =>
  import("../components/admin/SuperAdmin/Certificate/CreateCertificate").then(
    (module) => ({ default: module.AdminCreateCertificate })
  )
);
const AdminTrophy = lazy(() =>
  import("../components/admin/SuperAdmin/Trophy/Trophy").then((module) => ({
    default: module.AdminTrophy,
  }))
);
const AdminCreateTrophy = lazy(() =>
  import("../components/admin/SuperAdmin/Trophy/CreateTrophy").then(
    (module) => ({ default: module.AdminCreateTrophy })
  )
);
const AdminEditTrophy = lazy(() =>
  import("../components/admin/SuperAdmin/Trophy/EditTrophy").then((module) => ({
    default: module.AdminEditTrophy,
  }))
);
const AdminEditCertificate = lazy(() =>
  import("../components/admin/SuperAdmin/Certificate/EditCertificate").then(
    (module) => ({ default: module.AdminEditCertificate })
  )
);
const AdminSubscription = lazy(() =>
  import("../components/admin/SuperAdmin/Subscription/Subscription").then(
    (module) => ({ default: module.AdminSubscription })
  )
);
const AdminCoupon = lazy(() =>
  import("../components/admin/SuperAdmin/Coupon/Coupon").then((module) => ({
    default: module.AdminCoupon,
  }))
);
const AdminCreateCoupon = lazy(() =>
  import("../components/admin/SuperAdmin/Coupon/CreateCoupon").then(
    (module) => ({ default: module.AdminCreateCoupon })
  )
);
const AdminEditCoupon = lazy(() =>
  import("../components/admin/SuperAdmin/Coupon/EditCoupon").then((module) => ({
    default: module.AdminEditCoupon,
  }))
);
const AdminViewQuestInfo = lazy(() =>
  import("../components/admin/SuperAdmin/Student/ViewQuestInfo").then(
    (module) => ({ default: module.AdminViewQuestInfo })
  )
);
const AdminFormEnquires = lazy(() =>
  import("../components/admin/SuperAdmin/Enquires/FormEnquires").then(
    (module) => ({ default: module.AdminFormEnquires })
  )
);
const AdminTraffic = lazy(() =>
  import("../components/admin/SuperAdmin/TrafficSource/Traffic").then(
    (module) => ({ default: module.AdminTraffic })
  )
);
const AdminCreateTraffic = lazy(() =>
  import("../components/admin/SuperAdmin/TrafficSource/CreateTraffic").then(
    (module) => ({ default: module.AdminCreateTraffic })
  )
);
const AdminEditTraffic = lazy(() =>
  import("../components/admin/SuperAdmin/TrafficSource/EditTraffic").then(
    (module) => ({ default: module.AdminEditTraffic })
  )
);
const AdminTrafficDetail = lazy(() =>
  import("../components/admin/SuperAdmin/TrafficSource/TrafficDetail").then(
    (module) => ({ default: module.AdminTrafficDetail })
  )
);
const AdminStudentsOfParent = lazy(() =>
  import("../components/admin/SuperAdmin/Student/StudentsOfParent").then(
    (module) => ({ default: module.AdminStudentsOfParent })
  )
);
const AdminUserSubscriptionHistory = lazy(() =>
  import(
    "../components/admin/SuperAdmin/Subscription/UserSubscriptionHistoryPage"
  ).then((module) => ({ default: module.AdminUserSubscriptionHistory }))
);
const AdminParentSubscriptionHistory = lazy(() =>
  import("../components/admin/SuperAdmin/Parents/ViewSubscription").then(
    (module) => ({ default: module.AdminParentSubscriptionHistory })
  )
);
const AdminReferral = lazy(() =>
  import("../components/admin/SuperAdmin/Referral/Referral").then((module) => ({
    default: module.AdminReferral,
  }))
);
const AdminCreateReferral = lazy(() =>
  import("../components/admin/SuperAdmin/Referral/CreateReferral").then(
    (module) => ({ default: module.AdminCreateReferral })
  )
);
const AdminEditReferral = lazy(() =>
  import("../components/admin/SuperAdmin/Referral/EditReferral").then(
    (module) => ({ default: module.AdminEditReferral })
  )
);

const UpdateSchool = lazy(() =>
  import("../components/admin/SuperAdmin/School/UpdateSchool").then(
    (module) => ({ default: module.UpdateSchool })
  )
);

const SuperAdminRoutes = () => (
  <>
    <Route
      path="/admin/dashboard"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminDashboard />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/certificate"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCertificate />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/certificate/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateCertificate />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/certificate/edit/:certificateId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditCertificate />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/trophy"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminTrophy />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/trophy/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateTrophy />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/trophy/edit/:trophyId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditTrophy />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/quest"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminQuest />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/quest/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateQuest />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/quest/edit/:questId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditQuest />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/view_quest"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminViewQuest />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/user"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminUser />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/user/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateUser />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/user/edit/:userId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditUser />
          </Suspense>
        </ProtectedRoute>
      }
    />
    {/* Student Routes */}
    <Route
      path="/student"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminStudent />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/student/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateStudent />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/student/edit/:studentId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditStudent />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/view/student/:studentId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminViewStudent />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/view/student/:studentId/quest/:type"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminViewQuestInfo />
          </Suspense>
        </ProtectedRoute>
      }
    />
    {/* Teacher Routes */}
    <Route
      path="/teacher"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminTeacher />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/teacher/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateTeacher />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/teacher/edit"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditTeacher />
          </Suspense>
        </ProtectedRoute>
      }
    />
    {/* Parent Routes */}
    <Route
      path="/parent"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminParents />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/parent/student/:parentId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminStudentsOfParent />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/parent/subscription/:parentId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminParentSubscriptionHistory />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/parent/profile"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminViewParent />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/parent/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateParent />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/parent/edit/:parentId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditParent />
          </Suspense>
        </ProtectedRoute>
      }
    />
    {/* School Routes */}
    <Route
      path="/school"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminSchool />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/school/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateSchool />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/school/edit/:id"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <UpdateSchool />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/school/profile/:schoolId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminViewSchool />
          </Suspense>
        </ProtectedRoute>
      }
    />
    {/* School Agreement Routes */}
    <Route
      path="/school/agreements"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminAllAgreements />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/school/agreement/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateAgreement />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/school/agreement/edit/:agreementId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminUpdateAgreement />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/school/agreement/invoices"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminAllAgreementInvoices />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/school/agreement/view/invoice/:invoiceId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminAgreementViewInvoice />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/school/agreement/payments"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminAgreementInvoicePayments />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/school/agreement/create-payment"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AgreementCreatePayment />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/school/view/payment/:paymentId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminViewInvoicePayment />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/admin/notification"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminNotifications />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/notification/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateNotification />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/quizzes/:chapterId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminQuizzes />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/quiz/create/:chapterId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateQuizz />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/quiz/edit/:chapterId/:quizzId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditQuizz />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/quiz/view/:quizzId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminViewQuizz />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/create-chapter/:questId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateChapter />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/view/chapter/:questId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminViewChapter />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/edit/chapter/:questId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditChapter />
          </Suspense>
        </ProtectedRoute>
      }
    />
    {/* Admin Account Routes  */}
    <Route
      path="/admin/settings"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminSettings />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/user/roles"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminUserRoles />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/role/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateRole />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/support/tickets"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminTickets />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/reports"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminReports />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/subscription"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminSubscription />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/subscription/parent/:parentId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminUserSubscriptionHistory />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/coupon"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCoupon />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/coupon/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateCoupon />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/coupon/edit/:couponId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditCoupon />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/traffic-source"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminTraffic />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/traffic-source/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateTraffic />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/traffic-source/edit/:trafficId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditTraffic />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/traffic-source/detail/:trafficId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminTrafficDetail />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/form-enquires"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminFormEnquires />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/referral"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminReferral />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/referral/create"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminCreateReferral />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/referral/edit/:referralId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminEditReferral />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="/referral/detail/:referralId"
      element={
        <ProtectedRoute requiredRole={[1, 14]}>
          <Suspense fallback={<Loader />}>
            <AdminTrafficDetail />
          </Suspense>
        </ProtectedRoute>
      }
    />
  </>
);

export default SuperAdminRoutes;
